"use client";

/* La ricerca della pagina di un Paese (§ 3 delle istruzioni): lo stesso
   riquadro della pagina Report (`RicercaReport`, § 2 di
   ISTRUZIONI-report.md), con l'etichetta, il campo e «Cerca ↗» pieno nel
   mare; sotto, al posto delle righe di filtri, tre tendine in fila:
   Categoria coi numeri del Paese, Tipo di dispaccio, Periodo.

   Tutto nell'indirizzo (`?q`, `?cat`, `?tipo`, `?date`, lib/paese-stato.ts),
   e la rotta rifà la domanda a monte. Il testo parte all'invio o al
   pulsante; una tendina parte da sola, 400 ms dopo l'ultimo cambio
   (`RITARDO_FILTRI`: due `router.push` in volo insieme fanno vincere il
   primo). Senza JavaScript il riquadro è un GET verso la stessa pagina con
   gli stessi nomi, e «Cerca» manda anche le tendine. */

import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, type FormEvent } from "react";

import { ETICHETTA, FUOCO_HOME } from "@/components/home/stile";
import { PERIODI, RITARDO_FILTRI } from "@/lib/constants";
import {
  ETICHETTA_ESCLUSIVI,
  TIPI_MENU,
  hrefPaese,
  type StatoPaese,
  type TipoFiltro,
} from "@/lib/paese-stato";

const TENDINA = `block w-full min-w-0 cursor-pointer rounded-[2px] border border-rule bg-bg px-3 py-2.5 font-sans text-corpo text-ink ${FUOCO_HOME}`;

export function RicercaPaese({
  slug,
  nome,
  stato,
  categorie,
}: {
  slug: string;
  nome: string;
  stato: StatoPaese;
  categorie: { nome: string; quanti: number }[];
}) {
  const router = useRouter();
  /* Specchi riallineati all'indirizzo durante il render: tornando indietro
     o azzerando i filtri i campi riprendono il valore vero. */
  const [visto, setVisto] = useState(stato);
  const [testo, setTesto] = useState(stato.q);
  const [scelte, setScelte] = useState(stato);
  if (visto !== stato) {
    setVisto(stato);
    setTesto(stato.q);
    setScelte(stato);
  }

  const attesa = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (attesa.current) clearTimeout(attesa.current);
  }, []);

  const cambia = (modifica: Partial<StatoPaese>) => {
    const nuovo = { ...scelte, ...modifica };
    setScelte(nuovo);
    if (attesa.current) clearTimeout(attesa.current);
    attesa.current = setTimeout(() => {
      router.push(hrefPaese(slug, { ...nuovo, q: testo.trim() }), { scroll: false });
    }, RITARDO_FILTRI);
  };

  const cerca = (e: FormEvent) => {
    e.preventDefault();
    if (attesa.current) clearTimeout(attesa.current);
    router.push(hrefPaese(slug, { ...scelte, q: testo.trim() }), { scroll: false });
  };

  /* Una categoria arrivata dall'indirizzo che il menu non conosce resta
     scelta e visibile. */
  const voci = scelte.cat && !categorie.some((c) => c.nome === scelte.cat) ? [{ nome: scelte.cat, quanti: 0 }, ...categorie] : categorie;

  return (
    <div role="search" aria-label={`Cerca nei dispacci · ${nome}`} className="mt-6 rounded-[3px] border border-rule bg-surface px-5 py-4">
      <form action={`/paesi/${slug}`} method="get" onSubmit={cerca} className="flex flex-col gap-3">
        <label htmlFor="pa-q" className={ETICHETTA}>
          Cerca nei dispacci · {nome}
        </label>
        <div className="flex">
          <input
            id="pa-q"
            name="q"
            type="search"
            value={testo}
            onChange={(e) => setTesto(e.target.value)}
            placeholder="Una parola, un’impresa o un argomento"
            autoComplete="off"
            enterKeyHint="search"
            /* 16 sul telefono: sotto i sedici iOS ingrandisce la pagina
               appena il campo prende il fuoco. */
            className={`min-w-0 flex-1 rounded-l-[2px] border border-r-0 border-sommesso bg-bg px-3.5 py-3 font-sans text-[16px] text-ink placeholder:text-sommesso min-[901px]:text-sommario ${FUOCO_HOME}`}
          />
          <button
            type="submit"
            className={`cursor-pointer rounded-r-[2px] border-0 bg-mare px-5 font-sans text-voce font-semibold text-suMare transition-opacity hover:opacity-90 ${FUOCO_HOME}`}
          >
            Cerca <span aria-hidden>↗</span>
          </button>
        </div>
        <div className="mt-0.5 grid grid-cols-1 gap-3.5 min-[901px]:grid-cols-3">
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className={ETICHETTA}>Categoria</span>
            <select name="cat" value={scelte.cat} onChange={(e) => cambia({ cat: e.target.value })} className={TENDINA}>
              <option value="">Tutte le categorie</option>
              {voci.map((c) => (
                <option key={c.nome} value={c.nome}>
                  {c.quanti > 0 ? `${c.nome} · ${c.quanti}` : c.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className={ETICHETTA}>Tipo di dispaccio</span>
            <select
              name="tipo"
              value={scelte.tipo}
              onChange={(e) => cambia({ tipo: e.target.value as TipoFiltro })}
              className={TENDINA}
            >
              {TIPI_MENU.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
              {scelte.tipo === "esclusivi" ? <option value="esclusivi">{ETICHETTA_ESCLUSIVI}</option> : null}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1.5">
            <span className={ETICHETTA}>Periodo</span>
            <select
              name="date"
              value={scelte.date}
              onChange={(e) => cambia({ date: e.target.value as StatoPaese["date"] })}
              className={TENDINA}
            >
              <option value="">Qualsiasi data</option>
              {PERIODI.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </form>
    </div>
  );
}
